import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getAvatarUrl } from '../utils/avatar';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTrophy, FaBolt, FaFire, FaCrown, FaPlus, FaClock, FaCheck, FaUsers, FaHandPointLeft, FaHandPointRight, FaTimes, FaEye } from 'react-icons/fa';
import { arenaAPI } from '../utils/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';

const formatTimeLeft = (expiresAt) => {
  if (!expiresAt) return '';
  const diff = new Date(expiresAt) - Date.now();
  if (diff <= 0) return 'Ended';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `${h}h ${m}m left`;
};

const VoteCard = ({ debate, onVote, onWatch }) => {
  const [voted, setVoted] = useState(debate.user_voted);
  const [hoveredSide, setHoveredSide] = useState(null);
  const totalVotes = debate.votes_challenger + debate.votes_opponent;
  const pctA = totalVotes > 0 ? Math.round((debate.votes_challenger / totalVotes) * 100) : 50;
  const pctB = totalVotes > 0 ? 100 - pctA : 50;

  const handleVote = async (side) => {
    if (voted) return;
    try {
      await onVote(debate.id, side);
      setVoted(side);
      toast.success('Vote cast!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to vote');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow"
      data-testid={`debate-card-${debate.id}`}
    >
      {/* Topic Header */}
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">{debate.category}</span>
          <div className="flex items-center space-x-1.5 text-xs text-gray-400">
            <FaClock size={10} />
            <span>{formatTimeLeft(debate.expires_at)}</span>
          </div>
        </div>
        <h3 className="text-base font-bold text-gray-900 leading-snug">{debate.topic}</h3>
        <div className="flex items-center justify-center my-2">
          <span className="text-xs text-gray-400">{totalVotes} vote{totalVotes !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* VS Battle Cards */}
      <div className="grid grid-cols-2 gap-0 relative">
        {/* VS Badge */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
          <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center shadow-lg border-4 border-white">
            <span className="text-white font-extrabold text-xs">VS</span>
          </div>
        </div>

        {/* Challenger Side */}
        <div
          onClick={() => handleVote('challenger')}
          onMouseEnter={() => setHoveredSide('challenger')}
          onMouseLeave={() => setHoveredSide(null)}
          data-testid={`vote-challenger-${debate.id}`}
          className={`relative p-4 transition-all border-r border-gray-100 ${
            voted === 'challenger' ? 'bg-emerald-50' : voted ? 'bg-gray-50 opacity-60' : hoveredSide === 'challenger' ? 'bg-blue-50' : 'hover:bg-gray-50'
          } ${!voted ? 'cursor-pointer' : 'cursor-default'}`}
        >
          <div className="flex flex-col items-center text-center space-y-2">
            <Link to={`/profile/${debate.challenger_username}`} onClick={e => e.stopPropagation()}>
              <img
                src={getAvatarUrl(debate.challenger_avatar, debate.challenger_name)}
                alt=""
                className={`w-12 h-12 rounded-full object-cover ring-2 transition-all ${
                  voted === 'challenger' ? 'ring-emerald-400' : hoveredSide === 'challenger' ? 'ring-blue-400' : 'ring-gray-200'
                }`}
              />
            </Link>
            <div>
              <Link to={`/profile/${debate.challenger_username}`} onClick={e => e.stopPropagation()} className="hover:underline">
                <p className="text-sm font-bold text-gray-900 truncate max-w-[120px]">{debate.challenger_name}</p>
              </Link>
              <p className="text-[11px] text-gray-400">@{debate.challenger_username}</p>
            </div>
            {debate.challenger_argument && (
              <p className="text-sm text-gray-700 line-clamp-4 leading-relaxed">
                {debate.challenger_argument.text}
              </p>
            )}
            {debate.challenger_argument?.media_url && (
              <div className="w-full rounded-lg overflow-hidden mt-1">
                {debate.challenger_argument.media_type?.startsWith('image') ? (
                  <img src={debate.challenger_argument.media_url} alt="" className="w-full h-24 object-cover rounded-lg" />
                ) : debate.challenger_argument.media_type?.startsWith('video') ? (
                  <video src={debate.challenger_argument.media_url} controls className="w-full h-24 rounded-lg" />
                ) : null}
              </div>
            )}
          </div>

          {voted && (
            <div className="mt-3 pt-2 border-t border-gray-100">
              <div className="flex items-center justify-center space-x-1">
                <span className={`text-lg font-extrabold ${voted === 'challenger' ? 'text-emerald-500' : 'text-gray-400'}`}>{pctA}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                <div className="bg-emerald-500 h-1.5 rounded-full transition-all" style={{ width: `${pctA}%` }} />
              </div>
            </div>
          )}

          {!voted && hoveredSide === 'challenger' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 text-center">
              <span className="text-xs font-semibold text-blue-500">Tap to vote</span>
            </motion.div>
          )}

          {voted === 'challenger' && (
            <div className="absolute top-2 right-2">
              <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                <FaCheck className="text-white" size={9} />
              </div>
            </div>
          )}
        </div>

        {/* Opponent Side */}
        <div
          onClick={() => handleVote('opponent')}
          onMouseEnter={() => setHoveredSide('opponent')}
          onMouseLeave={() => setHoveredSide(null)}
          data-testid={`vote-opponent-${debate.id}`}
          className={`relative p-4 transition-all ${
            voted === 'opponent' ? 'bg-emerald-50' : voted ? 'bg-gray-50 opacity-60' : hoveredSide === 'opponent' ? 'bg-blue-50' : 'hover:bg-gray-50'
          } ${!voted ? 'cursor-pointer' : 'cursor-default'}`}
        >
          <div className="flex flex-col items-center text-center space-y-2">
            <Link to={`/profile/${debate.opponent_username}`} onClick={e => e.stopPropagation()}>
              <img
                src={getAvatarUrl(debate.opponent_avatar, debate.opponent_name)}
                alt=""
                className={`w-12 h-12 rounded-full object-cover ring-2 transition-all ${
                  voted === 'opponent' ? 'ring-emerald-400' : hoveredSide === 'opponent' ? 'ring-blue-400' : 'ring-gray-200'
                }`}
              />
            </Link>
            <div>
              <Link to={`/profile/${debate.opponent_username}`} onClick={e => e.stopPropagation()} className="hover:underline">
                <p className="text-sm font-bold text-gray-900 truncate max-w-[120px]">{debate.opponent_name}</p>
              </Link>
              <p className="text-[11px] text-gray-400">@{debate.opponent_username}</p>
            </div>
            {debate.opponent_argument && (
              <p className="text-sm text-gray-700 line-clamp-4 leading-relaxed">
                {debate.opponent_argument.text}
              </p>
            )}
            {debate.opponent_argument?.media_url && (
              <div className="w-full rounded-lg overflow-hidden mt-1">
                {debate.opponent_argument.media_type?.startsWith('image') ? (
                  <img src={debate.opponent_argument.media_url} alt="" className="w-full h-24 object-cover rounded-lg" />
                ) : debate.opponent_argument.media_type?.startsWith('video') ? (
                  <video src={debate.opponent_argument.media_url} controls className="w-full h-24 rounded-lg" />
                ) : null}
              </div>
            )}
          </div>

          {voted && (
            <div className="mt-3 pt-2 border-t border-gray-100">
              <div className="flex items-center justify-center space-x-1">
                <span className={`text-lg font-extrabold ${voted === 'opponent' ? 'text-emerald-500' : 'text-gray-400'}`}>{pctB}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                <div className="bg-emerald-500 h-1.5 rounded-full transition-all" style={{ width: `${pctB}%` }} />
              </div>
            </div>
          )}

          {!voted && hoveredSide === 'opponent' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 text-center">
              <span className="text-xs font-semibold text-blue-500">Tap to vote</span>
            </motion.div>
          )}

          {voted === 'opponent' && (
            <div className="absolute top-2 left-2">
              <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                <FaCheck className="text-white" size={9} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer with watch + vote hint */}
      <div className="px-5 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        {!voted ? (
          <div className="flex items-center space-x-2">
            <FaHandPointLeft className="text-gray-300" size={11} />
            <span className="text-[11px] text-gray-400 font-medium">Pick a side to vote</span>
            <FaHandPointRight className="text-gray-300" size={11} />
          </div>
        ) : (
          <span className="text-[11px] text-gray-400">Vote recorded</span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onWatch(debate.id); }}
          data-testid={`watch-debate-${debate.id}`}
          className="flex items-center space-x-1.5 text-[11px] text-gray-400 hover:text-gray-700 font-medium transition-colors"
        >
          <FaEye size={11} />
          <span>Watch</span>
        </button>
      </div>
    </motion.div>
  );
};

const CreateDebateModal = ({ onClose, onCreate }) => {
  const [topic, setTopic] = useState('');
  const [category, setCategory] = useState('open');
  const categories = ['open', 'tech', 'sports', 'culture', 'politics', 'entertainment', 'science'];

  const handleCreate = async () => {
    if (!topic.trim()) return toast.error('Enter a topic');
    try {
      await onCreate({ topic, category });
      toast.success('Debate created!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create debate');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md"
      >
        <div className="p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4" data-testid="create-debate-title">Start a Debate</h2>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1.5">Topic</label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g., Is AI going to replace developers?"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                data-testid="debate-topic-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1.5">Category</label>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setCategory(cat)}
                    data-testid={`category-${cat}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all capitalize ${
                      category === cat ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="flex space-x-3 p-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors" data-testid="cancel-debate-btn">Cancel</button>
          <button onClick={handleCreate} className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 transition-colors" data-testid="submit-debate-btn">Create Debate</button>
        </div>
      </motion.div>
    </div>
  );
};

const SubmitArgumentModal = ({ debate, onClose, onSubmit }) => {
  const [text, setText] = useState('');
  const isChal = debate._isChal;

  const handleSubmit = async () => {
    if (!text.trim()) return toast.error('Enter your argument');
    try {
      await onSubmit(debate.id, { text });
      toast.success('Argument submitted!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md"
      >
        <div className="p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Your Argument</h2>
          <p className="text-sm text-gray-500 mb-4">Topic: <span className="font-medium text-gray-700">{debate.topic}</span></p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Make your point..."
            rows={5}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 resize-none"
            data-testid="argument-text-input"
          />
          <p className="text-xs text-gray-400 mt-1">{text.length}/500 characters</p>
        </div>
        <div className="flex space-x-3 p-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-50" data-testid="cancel-argument-btn">Cancel</button>
          <button onClick={handleSubmit} className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800" data-testid="submit-argument-btn">Submit</button>
        </div>
      </motion.div>
    </div>
  );
};

const LeaderboardRow = ({ user, index }) => (
  <div className={`flex items-center space-x-3 px-4 py-3 ${index < 3 ? 'bg-amber-50/50' : ''} rounded-xl`} data-testid={`leaderboard-row-${index}`}>
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-extrabold ${
      index === 0 ? 'bg-amber-400 text-white' : index === 1 ? 'bg-gray-300 text-white' : index === 2 ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-500'
    }`}>
      {index < 3 ? <FaCrown size={11} /> : index + 1}
    </div>
    <Link to={`/profile/${user.username}`}>
      <img src={getAvatarUrl(user.avatar_url, user.full_name)} alt="" className="w-8 h-8 rounded-full object-cover hover:ring-2 hover:ring-[#4A90E2]/40 transition-all" />
    </Link>
    <div className="flex-1 min-w-0">
      <Link to={`/profile/${user.username}`} className="hover:underline">
        <p className="text-sm font-semibold text-gray-900 truncate">{user.full_name}</p>
      </Link>
      <p className="text-[11px] text-gray-400">@{user.username}</p>
    </div>
    <div className="text-right">
      <p className="text-sm font-bold text-gray-900">{user.arena_xp} XP</p>
      <p className="text-[11px] text-gray-400">{user.arena_wins}W / {user.arena_losses}L</p>
    </div>
  </div>
);

const Arena = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('active');
  const [debates, setDebates] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myStats, setMyStats] = useState({ arena_xp: 0, arena_wins: 0, arena_losses: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [argModal, setArgModal] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, lbRes] = await Promise.all([
        arenaAPI.getMyStats().catch(() => ({ data: { arena_xp: 0, arena_wins: 0, arena_losses: 0 } })),
        arenaAPI.getLeaderboard().catch(() => ({ data: [] })),
      ]);
      setMyStats(statsRes.data);
      setLeaderboard(lbRes.data);

      let debatesRes;
      if (tab === 'active') debatesRes = await arenaAPI.getActive();
      else if (tab === 'open') debatesRes = await arenaAPI.getOpen();
      else if (tab === 'my') debatesRes = await arenaAPI.getMy();
      else debatesRes = await arenaAPI.getCompleted();
      setDebates(debatesRes.data);
    } catch (err) {
      console.error('Arena load error', err);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async (data) => {
    await arenaAPI.createDebate(data);
    loadData();
  };

  const handleVote = async (debateId, side) => {
    await arenaAPI.vote(debateId, side);
    setDebates(prev => prev.map(d => {
      if (d.id !== debateId) return d;
      return {
        ...d,
        user_voted: side,
        votes_challenger: side === 'challenger' ? d.votes_challenger + 1 : d.votes_challenger,
        votes_opponent: side === 'opponent' ? d.votes_opponent + 1 : d.votes_opponent,
        total_votes: d.total_votes + 1,
      };
    }));
  };

  const handleJoin = async (debateId) => {
    try {
      await arenaAPI.joinDebate(debateId);
      toast.success('Joined! Submit your argument.');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join');
    }
  };

  const handleArgSubmit = async (debateId, data) => {
    await arenaAPI.submitArgument(debateId, data);
    loadData();
  };

  const handleAccept = async (debateId) => {
    try {
      await arenaAPI.acceptChallenge(debateId);
      toast.success('Challenge accepted! Submit your argument.');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    }
  };

  const handleDecline = async (debateId) => {
    try {
      await arenaAPI.declineChallenge(debateId);
      toast.success('Challenge declined');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline');
    }
  };

  const tabs = [
    { key: 'active', label: 'Live Votes', icon: FaBolt },
    { key: 'open', label: 'Open', icon: FaUsers },
    { key: 'my', label: 'My Debates', icon: FaFire },
    { key: 'completed', label: 'Results', icon: FaTrophy },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <Sidebar />
        <main className="col-span-12 md:col-span-10 pb-20 md:pb-0" data-testid="arena-page">
          <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 font-arthaus" data-testid="arena-title">PointArena</h1>
                <p className="text-sm text-gray-400 mt-0.5">Pick a side. Make your point. Win the crowd.</p>
              </div>
              <button
                onClick={() => setShowCreate(true)}
                data-testid="start-debate-btn"
                className="bg-gray-900 text-white font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-gray-800 transition-all hover:scale-[1.02] active:scale-95 flex items-center space-x-2"
              >
                <FaPlus size={11} />
                <span>Start Debate</span>
              </button>
            </div>

            {/* Stats Bar */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-white border border-gray-100 rounded-2xl p-4 text-center" data-testid="stat-xp">
                <p className="text-2xl font-extrabold text-gray-900">{myStats.arena_xp}</p>
                <p className="text-xs text-gray-400 font-medium">Arena XP</p>
              </div>
              <div className="bg-white border border-gray-100 rounded-2xl p-4 text-center" data-testid="stat-wins">
                <p className="text-2xl font-extrabold text-emerald-500">{myStats.arena_wins}</p>
                <p className="text-xs text-gray-400 font-medium">Wins</p>
              </div>
              <div className="bg-white border border-gray-100 rounded-2xl p-4 text-center" data-testid="stat-losses">
                <p className="text-2xl font-extrabold text-gray-400">{myStats.arena_losses}</p>
                <p className="text-xs text-gray-400 font-medium">Losses</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Main Content */}
              <div className="lg:col-span-8">
                {/* Tabs */}
                <div className="flex space-x-1 bg-gray-100 p-1 rounded-full w-fit mb-5 overflow-x-auto max-w-full" data-testid="arena-tabs">
                  {tabs.map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      onClick={() => setTab(key)}
                      data-testid={`arena-tab-${key}`}
                      className={`flex items-center space-x-1.5 px-3 sm:px-4 py-2 rounded-full font-medium transition-all text-xs sm:text-sm whitespace-nowrap ${
                        tab === key ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Icon size={12} />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>

                {/* Debate Cards */}
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="bg-white rounded-2xl h-52 animate-pulse border border-gray-100" />
                    ))}
                  </div>
                ) : debates.length === 0 ? (
                  <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center" data-testid="empty-arena">
                    <FaBolt className="text-3xl text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium mb-1">
                      {tab === 'active' ? 'No live debates right now' : tab === 'open' ? 'No open debates to join' : tab === 'my' ? "You haven't joined any debates yet" : 'No completed debates yet'}
                    </p>
                    <p className="text-gray-400 text-sm mb-4">
                      {tab === 'open' || tab === 'active' ? 'Start one and challenge the community!' : 'Get in the arena and make your point!'}
                    </p>
                    <button
                      onClick={() => setShowCreate(true)}
                      className="bg-gray-900 text-white font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-gray-800"
                    >
                      Start a Debate
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="debates-container">
                    {debates.map(debate => (
                      <div key={debate.id}>
                        {(tab === 'active' || tab === 'completed') && debate.challenger_argument && debate.opponent_argument ? (
                          <VoteCard debate={debate} onVote={handleVote} onWatch={(id) => navigate(`/arena/${id}`)} />
                        ) : tab === 'open' ? (
                          <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">{debate.category}</span>
                                <h3 className="text-base font-bold text-gray-900 mt-2">{debate.topic}</h3>
                                <p className="text-xs text-gray-400 mt-1">by @{debate.challenger_username}</p>
                              </div>
                              <button
                                onClick={() => handleJoin(debate.id)}
                                data-testid={`join-debate-${debate.id}`}
                                className="bg-gray-900 text-white font-semibold rounded-full px-4 py-2 text-xs hover:bg-gray-800 transition-all"
                              >
                                Join & Debate
                              </button>
                            </div>
                          </motion.div>
                        ) : tab === 'my' ? (
                          <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white border border-gray-100 rounded-2xl p-5"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">{debate.category}</span>
                                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${
                                    debate.status === 'voting' ? 'bg-blue-100 text-blue-600' :
                                    debate.status === 'completed' ? 'bg-emerald-100 text-emerald-600' :
                                    debate.status === 'challenged' ? 'bg-purple-100 text-purple-600' :
                                    debate.status === 'declined' ? 'bg-red-100 text-red-500' :
                                    debate.status === 'open' ? 'bg-amber-100 text-amber-600' :
                                    'bg-gray-100 text-gray-500'
                                  }`}>{debate.status}</span>
                                </div>
                                <h3 className="text-base font-bold text-gray-900 mt-1">{debate.topic}</h3>
                                <p className="text-xs text-gray-400 mt-1">
                                  {debate.challenger_name} vs {debate.opponent_name || 'Waiting for opponent...'}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                {debate.status === 'challenged' && debate.opponent_id === JSON.parse(sessionStorage.getItem('user') || '{}').id && (
                                  <>
                                    <button
                                      onClick={() => handleAccept(debate.id)}
                                      data-testid={`accept-challenge-${debate.id}`}
                                      className="bg-emerald-500 text-white font-semibold rounded-full px-4 py-2 text-xs hover:bg-emerald-600 transition-all flex items-center space-x-1.5"
                                    >
                                      <FaCheck size={10} />
                                      <span>Accept</span>
                                    </button>
                                    <button
                                      onClick={() => handleDecline(debate.id)}
                                      data-testid={`decline-challenge-${debate.id}`}
                                      className="bg-white border border-gray-200 text-gray-500 font-semibold rounded-full px-4 py-2 text-xs hover:bg-red-50 hover:text-red-500 hover:border-red-200 transition-all flex items-center space-x-1.5"
                                    >
                                      <FaTimes size={10} />
                                      <span>Decline</span>
                                    </button>
                                  </>
                                )}
                                {debate.status === 'challenged' && debate.challenger_id === JSON.parse(sessionStorage.getItem('user') || '{}').id && (
                                  <span className="text-xs text-purple-500 font-medium bg-purple-50 px-3 py-1.5 rounded-full">Awaiting response</span>
                                )}
                                {debate.status === 'pending' && (
                                  <button
                                    onClick={() => setArgModal({ ...debate, _isChal: true })}
                                    data-testid={`submit-arg-${debate.id}`}
                                    className="bg-gray-900 text-white font-semibold rounded-full px-4 py-2 text-xs hover:bg-gray-800"
                                  >
                                    Submit Argument
                                  </button>
                                )}
                                {debate.status === 'completed' && (
                                  <div className="text-right">
                                    <p className="text-sm font-bold">{debate.votes_challenger} - {debate.votes_opponent}</p>
                                    <p className="text-xs text-gray-400">{debate.total_votes} votes</p>
                                  </div>
                                )}
                                {debate.status === 'declined' && (
                                  <span className="text-xs text-red-400 font-medium">Declined</span>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        ) : (
                          <VoteCard debate={debate} onVote={handleVote} onWatch={(id) => navigate(`/arena/${id}`)} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Leaderboard Sidebar */}
              <div className="lg:col-span-4">
                <div className="bg-white border border-gray-100 rounded-2xl p-4 sticky top-6" data-testid="leaderboard">
                  <div className="flex items-center space-x-2 mb-4">
                    <FaTrophy className="text-amber-400" size={14} />
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Leaderboard</h3>
                  </div>
                  {leaderboard.length > 0 ? (
                    <div className="space-y-1">
                      {leaderboard.map((user, i) => (
                        <LeaderboardRow key={user.id} user={user} index={i} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FaTrophy className="text-2xl text-gray-200 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">No rankings yet</p>
                      <p className="text-xs text-gray-300">Win debates to climb the board</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {showCreate && <CreateDebateModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />}
      {argModal && <SubmitArgumentModal debate={argModal} onClose={() => setArgModal(null)} onSubmit={handleArgSubmit} />}
    </div>
  );
};

export default Arena;
